import type { Workbook, Worksheet } from 'exceljs';
import dayjs from 'dayjs';
import type { FilterField, FilterLineType } from '../../../../../components/new-design/vyTableFilter';

export type AppliedFiltersSheetInput = {
  searchText: string | null | undefined;
  fromDate: Date | null;
  recognizedUpTo: Date | null;
  filters: FilterLineType[];
  filterFields: FilterField[];
};

export function buildAppliedFiltersSheet(
  workbook: Workbook,
  input: AppliedFiltersSheetInput,
): void {
  const worksheet = workbook.addWorksheet('Applied Filters');

  writeAppliedFiltersHeader(worksheet);
  writeStandardFilterRows(worksheet, input);
  writeDynamicFilterRows(worksheet, input.filters, input.filterFields);
  writeGeneratedAtRow(worksheet);
  applyAppliedFiltersColumnStyles(worksheet);
}

function writeAppliedFiltersHeader(worksheet: Worksheet): void {
  worksheet.addRow(['Field', 'Value']);
  worksheet.getRow(1).font = { bold: true };
}

function writeStandardFilterRows(worksheet: Worksheet, input: AppliedFiltersSheetInput): void {
  worksheet.addRow(['Date Range', formatDateRange(input.fromDate, input.recognizedUpTo)]);
  worksheet.addRow(['Search', formatSearchText(input.searchText)]);
}

function writeDynamicFilterRows(
  worksheet: Worksheet,
  filters: FilterLineType[],
  filterFields: FilterField[],
): void {
  for (const filter of filters) {
    const matchingField = filterFields.find((filterField) => filterField.id === filter.field);
    const fieldLabel = matchingField?.label ?? filter.field;
    worksheet.addRow([fieldLabel, formatFilterValue(filter, matchingField)]);
  }
}

function writeGeneratedAtRow(worksheet: Worksheet): void {
  worksheet.addRow(['Generated At', dayjs().format('MMM D, YYYY [at] h:mm A')]);
}

function applyAppliedFiltersColumnStyles(worksheet: Worksheet): void {
  worksheet.getColumn(1).width = 24;
  worksheet.getColumn(2).width = 60;
}

function formatDateRange(fromDate: Date | null, recognizedUpTo: Date | null): string {
  if (!fromDate && !recognizedUpTo) return 'All time';

  const fromLabel = fromDate ? dayjs(fromDate).format('MMM YYYY') : 'beginning';
  const toLabel = recognizedUpTo ? dayjs(recognizedUpTo).format('MMM YYYY') : 'present';

  return `${fromLabel} – ${toLabel}`;
}

function formatSearchText(searchText: string | null | undefined): string {
  return searchText?.trim() ? `"${searchText.trim()}"` : '—';
}

function formatFilterValue(filter: FilterLineType, filterField: FilterField | undefined): string {
  const operatorLabel = filterField?.operators.find((op) => op.value === filter.operator)?.label ?? filter.operator;
  return `${operatorLabel}: ${formatRawFilterValue(filter.value)}`;
}

function formatRawFilterValue(value: FilterLineType['value']): string {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  return JSON.stringify(value);
}
